import { styled } from "@mui/material/styles";

import { Radio } from "@/common/components/Radio/Radio";

import { FieldError } from "../FieldParts";
import { Container, Label } from "../shared.styles";
import { useSubmitFirstField } from "../useSubmitFirstField";

import type { FieldBaseProps, Option } from "../types";

type RadioOption<T> = Option<T> & {
  "data-testid"?: string;
};

type RadioGroupBaseProps<T> = Omit<FieldBaseProps, "placeholder"> & {
  options: RadioOption<T>[];
  value: T;
  onChange?: (value: T) => void;
};

const RadioGroupBase = <T,>({
  disabled,
  error,
  fullWidth,
  label,
  name,
  onBlur,
  onChange,
  options,
  value,
}: RadioGroupBaseProps<T>): React.ReactElement => (
  <Container
    $fullWidth={fullWidth}
    aria-labelledby={label ? `${name}-label` : undefined}
    data-testid={name}
    role="radiogroup"
  >
    {label ? (
      <Label as="span" id={`${name}-label`}>
        {label}
      </Label>
    ) : null}
    {options.map(({ label: optionLabel, value: optionValue, ...rest }) => (
      <RadioLabel key={String(optionValue)}>
        <Radio
          checked={optionValue === value}
          data-testid={`${name}-${String(optionValue)}`}
          disabled={disabled}
          name={name}
          onBlur={onBlur}
          onChange={() => onChange?.(optionValue)}
          value={String(optionValue)}
          {...rest}
        />
        {optionLabel}
      </RadioLabel>
    ))}
    <FieldError error={error} name={name} />
  </Container>
);

type RadioGroupProps<T> = Omit<
  RadioGroupBaseProps<T>,
  "error" | "onBlur" | "onChange" | "value"
> & {
  onValueChange?: (value: T) => void;
};

const RadioGroup = <T,>({
  name,
  onValueChange,
  ...props
}: RadioGroupProps<T>): React.ReactElement => {
  const { error, handleBlur, setValue, value } = useSubmitFirstField<T>(name);

  const handleChange = (next: T): void => {
    setValue(next);
    onValueChange?.(next);
  };

  return (
    <RadioGroupBase<T>
      {...props}
      error={error}
      name={name}
      onBlur={handleBlur}
      onChange={handleChange}
      value={value}
    />
  );
};

const RadioLabel = styled(Label)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  lineHeight: 1.4,
  cursor: "pointer",
}));

export {
  RadioGroup,
  RadioGroupBase,
  type RadioGroupBaseProps,
  type RadioGroupProps,
  type RadioOption,
};
